import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Flame, TrendingUp, TrendingDown } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '@/hooks/useAuth';
import { useComprehensiveNutrition } from '@/contexts/NutritionContext';
import { getHealthConnectedState } from '@/lib/appleHealth';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { getInclusiveLocalDayRange } from '@/lib/localDateRange';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
  Cell,
} from 'recharts';

type TimeRange = '7d' | '30d' | '90d';

interface DayData {
  date: string;
  displayDate: string;
  caloriesIn: number;
  caloriesOut: number;
  surplus: number;
}

function CalorieBalanceTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: DayData }>;
}) {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const rounded = Math.round(row.surplus);
  const balanceLine =
    rounded < 0
      ? t('components.charts.calorieBalance.tooltipDeficit', { value: rounded })
      : rounded > 0
        ? t('components.charts.calorieBalance.tooltipSurplus', { value: rounded })
        : t('components.charts.calorieBalance.tooltipBalance');
  return (
    <div
      className="rounded-lg border border-border p-3 text-sm shadow-md max-w-[220px]"
      style={{
        backgroundColor: "hsl(var(--background))",
        color: "hsl(var(--foreground))",
        opacity: 1,
      }}
    >
      <p className="font-medium mb-1">{row.displayDate}</p>
      <p className="font-semibold text-primary">{balanceLine}</p>
      <p className="text-muted-foreground text-xs mt-1.5">
        {t('components.charts.calorieBalance.tooltipInOut', { in: Math.round(row.caloriesIn), out: Math.round(row.caloriesOut) })}
      </p>
    </div>
  );
}

export const CalorieSurplusDeficitChart = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const nutritionHookData = useComprehensiveNutrition();
  const [connected, setConnected] = useState<boolean | null>(null);
  const [timeRange, setTimeRange] = useState<TimeRange>('7d');
  const [chartData, setChartData] = useState<DayData[]>([]);
  const [loading, setLoading] = useState(true);
  /** Tap/click a bar to emphasize it vs the zero line */
  const [focusedBarIndex, setFocusedBarIndex] = useState<number | null>(null);

  const isIOS = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
  const getNutritionTotalsRange = nutritionHookData?.getNutritionTotalsRange;

  useEffect(() => {
    if (!isIOS || !user?.id) {
      setConnected(false);
      return;
    }
    getHealthConnectedState(user.id).then(setConnected);
  }, [isIOS, user?.id]);

  useEffect(() => {
    if (!connected || !user?.id || !getNutritionTotalsRange) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
        const { startDate, endDate, startStr, endStr } = getInclusiveLocalDayRange(days);

        const [nutritionRes, healthRes] = await Promise.all([
          getNutritionTotalsRange(days),
          supabase
            .from('health_metrics')
            .select('metric_date, basal_energy_kcal, active_energy_kcal')
            .eq('user_id', user.id)
            .gte('metric_date', startStr)
            .lte('metric_date', endStr)
            .order('metric_date', { ascending: true }),
        ]);

        const nutritionByDate = new Map<string, number>();
        for (const day of nutritionRes ?? []) {
          const d = (day as { date: string; totals: { calories: number } }).date;
          const c = (day as { date: string; totals: { calories: number } }).totals?.calories ?? 0;
          nutritionByDate.set(d, c);
        }

        const healthByDate = new Map<string, { basal: number; active: number }>();
        for (const row of healthRes.data ?? []) {
          const d = (row as { metric_date: string; basal_energy_kcal: number | null; active_energy_kcal: number | null }).metric_date;
          const basal = (row as { metric_date: string; basal_energy_kcal: number | null; active_energy_kcal: number | null }).basal_energy_kcal ?? 0;
          const active = (row as { metric_date: string; basal_energy_kcal: number | null; active_energy_kcal: number | null }).active_energy_kcal ?? 0;
          healthByDate.set(d, { basal, active });
        }

        const result: DayData[] = [];
        const cursor = new Date(startDate);
        while (cursor <= endDate) {
          const dateStr = format(cursor, 'yyyy-MM-dd');
          const caloriesIn = nutritionByDate.get(dateStr) ?? 0;
          const { basal = 0, active = 0 } = healthByDate.get(dateStr) ?? {};
          const caloriesOut = basal + active;
          const surplus = caloriesIn - caloriesOut;
          result.push({
            date: dateStr,
            displayDate: days <= 7 ? format(cursor, 'EEE') : format(cursor, 'MMM d'),
            caloriesIn,
            caloriesOut,
            surplus,
          });
          cursor.setDate(cursor.getDate() + 1);
        }
        setChartData(result);
      } catch (e) {
        console.error('[CalorieSurplusDeficitChart]', e);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [connected, user?.id, timeRange, getNutritionTotalsRange]);

  useEffect(() => {
    setFocusedBarIndex(null);
  }, [timeRange, chartData.length]);

  if (!isIOS || connected !== true) return null;

  const avgSurplus = chartData.length > 0
    ? Math.round(chartData.reduce((s, d) => s + d.surplus, 0) / chartData.length)
    : 0;

  return (
    <Card className="mt-6 border-0">
      <CardHeader className="pb-2">
        <div className="flex flex-col gap-2">
          <CardTitle className="flex items-center gap-2 font-normal text-base">
            <Flame className="h-5 w-5 text-primary" />
            {t('components.charts.calorieBalance.title')}
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            {t('components.charts.calorieBalance.subtitle')}
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant={timeRange === '7d' ? 'default' : 'ghost'}
              size="sm"
              className="h-6 px-2 text-[11px]"
              onClick={() => setTimeRange('7d')}
            >
              {t('components.charts.calorieBalance.rangeL7D')}
            </Button>
            <Button
              variant={timeRange === '30d' ? 'default' : 'ghost'}
              size="sm"
              className="h-6 px-2 text-[11px]"
              onClick={() => setTimeRange('30d')}
            >
              {t('components.charts.calorieBalance.rangeL30D')}
            </Button>
            <Button
              variant={timeRange === '90d' ? 'default' : 'ghost'}
              size="sm"
              className="h-6 px-2 text-[11px]"
              onClick={() => setTimeRange('90d')}
            >
              {t('components.charts.calorieBalance.rangeL3M')}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        <div className="flex gap-4 mb-4 text-sm">
          <div className="flex items-center gap-2">
            {avgSurplus >= 0 ? (
              <TrendingUp className="h-4 w-4 text-amber-500" />
            ) : (
              <TrendingDown className="h-4 w-4 text-green-500" />
            )}
            <span className="text-muted-foreground">{t('components.charts.calorieBalance.avgBalance')}</span>
            <span className={`font-medium ${avgSurplus >= 0 ? 'text-amber-500' : 'text-green-500'}`}>
              {avgSurplus >= 0 ? '+' : ''}{avgSurplus} cal
            </span>
            <span className="text-muted-foreground text-xs">
              {avgSurplus > 0 ? t('components.charts.calorieBalance.surplusGaining') : t('components.charts.calorieBalance.deficitLosing')}
            </span>
          </div>
        </div>
        <div
          className="h-[200px] sm:h-[250px]"
          onMouseLeave={() => setFocusedBarIndex(null)}
        >
          {loading ? (
            <div className="h-full flex items-center justify-center text-muted-foreground">
              {t('common.loading')}
            </div>
          ) : chartData.length === 0 ? (
            <div className="h-full flex items-center justify-center text-muted-foreground">
              {t('components.charts.calorieBalance.emptyPeriod')}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 24, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis
                  dataKey="displayDate"
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                  tickLine={false}
                  axisLine={{ stroke: 'hsl(var(--border))' }}
                  interval={chartData.length > 14 ? Math.ceil(chartData.length / 10) : 0}
                />
                <YAxis
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => `${v >= 0 ? '+' : ''}${v}`}
                />
                <Tooltip
                  content={<CalorieBalanceTooltip />}
                  cursor={{
                    fill: 'hsl(var(--primary) / 0.22)',
                    stroke: 'hsl(var(--primary))',
                    strokeWidth: 1.5,
                    strokeDasharray: '4 3',
                  }}
                />
                <ReferenceLine
                  y={0}
                  stroke="hsl(var(--foreground))"
                  strokeWidth={2}
                  strokeDasharray="6 5"
                  label={{
                    value: t('components.charts.calorieBalance.balanceZero'),
                    position: 'insideTopRight',
                    fill: 'hsl(var(--foreground))',
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                />
                <Bar
                  dataKey="surplus"
                  name={t('components.charts.calorieBalance.surplus')}
                  radius={[2, 2, 0, 0]}
                  onClick={(_entry, index: number) => {
                    setFocusedBarIndex((prev) => (prev === index ? null : index));
                  }}
                >
                  {chartData.map((entry, i) => {
                    const base =
                      entry.surplus >= 0 ? 'hsl(45, 93%, 47%)' : 'hsl(142, 76%, 36%)';
                    const dimmed = focusedBarIndex !== null && focusedBarIndex !== i;
                    return (
                      <Cell
                        key={entry.date}
                        fill={base}
                        fillOpacity={dimmed ? 0.38 : 1}
                        stroke={focusedBarIndex === i ? 'hsl(var(--foreground))' : 'transparent'}
                        strokeWidth={focusedBarIndex === i ? 3 : 0}
                        style={{ transition: 'fill-opacity 0.15s ease' }}
                      />
                    );
                  })}
                </Bar>
                <Legend formatter={() => t('components.charts.calorieBalance.dailySurplusDeficit')} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
